import React, { useEffect, useCallback, useMemo, useId } from 'react';
import '../../../style/priceUpdateConfirmation.css';

const IconWarning = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FE9400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconArrow = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const IconLink = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2F80ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const getVariantId = (variant) => variant.id || variant.sku;

const PriceUpdateConfirmation = ({
  isOpen,
  onCancel,
  onApply,
  currentPrice,
  newPrice,
  linkedVariants = [],
  isSubmitting = false,
}) => {
  const titleId = useId();
  const linkedCount = linkedVariants.length;
  const current = parseFloat(currentPrice) || 0;
  const next = parseFloat(newPrice) || 0;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  const { diff, diffPercent, diffSign, isIncrease } = useMemo(() => {
    const d = next - current;
    const pct = current !== 0 ? ((d / current) * 100).toFixed(2) : '0';
    const sign = d >= 0 ? '+' : '-';
    return { diff: d, diffPercent: pct, diffSign: sign, isIncrease: d >= 0 };
  }, [current, next]);

  const handleConfirm = useCallback(async () => {
    if (isSubmitting || !onApply) return;
    await onApply(linkedVariants.map(getVariantId));
  }, [onApply, linkedVariants, isSubmitting]);

  if (!isOpen) return null;

  return (
    <div
      className="puc-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="puc-container step-1">
        <div className="puc-body">
          <div className="puc-warning-wrap">
            <IconWarning />
          </div>
          <h2 id={titleId} className="puc-title">Confirm Price Update</h2>
          <p className="puc-desc">You&apos;re about to change the selling price for this product</p>

          <div className="puc-comparison-box">
            <span className="puc-comp-header">Selling Price Change</span>
            <div className="puc-price-row">
              <div className="puc-price-card current">
                <span className="puc-card-label">CURRENT PRICE</span>
                <span className="puc-card-val">€ {current.toFixed(2)}</span>
              </div>
              <div className="puc-arrow-wrap">
                <IconArrow />
              </div>
              <div className="puc-price-card new">
                <span className="puc-card-label">NEW PRICE</span>
                <span className="puc-card-val">€ {next.toFixed(2)}</span>
              </div>
            </div>
            <div className={`puc-diff-badge${isIncrease ? '' : ' decrease'}`}>
              {diffSign} € {Math.abs(diff).toFixed(2)} ({diffSign}{Math.abs(diffPercent)}%)
            </div>
          </div>

          {linkedCount > 0 && (
            <div className="puc-info-bar">
              <IconLink />
              <span className="puc-info-text">
                {linkedCount} linked variant{linkedCount === 1 ? '' : 's'} will be updated to €{next.toFixed(2)}.
              </span>
            </div>
          )}
        </div>

        <div className="puc-footer">
          <button
            type="button"
            className="puc-cancel-btn"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="puc-continue-btn"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Yes, Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceUpdateConfirmation;
